import React from 'react';
import { useTranslation } from 'react-i18next';
import { Money, Portfolio, ShoppingCart, Time } from '@carbon/react/icons';
import { useMoney } from 'src/hooks/useMoney';
import { SelfEmployeeIncomes } from 'src/hooks/useSelfEmployee';
import { EmployeeEntity } from 'src/services/apis/types';

/**
 * The staff-facing summary on `/me`.
 *
 * Deliberately NOT `EmployeeIncomes`: that component is a stub whose "Total incomes",
 * "Total Bonus", "Total Fines" and "Total Day Off" tiles are a hardcoded `8000000`.
 * On an owner's dashboard that reads as an obvious placeholder; on a staff member's own page
 * it would be telling them their pay in invented figures. It stays untouched for the
 * owner-facing screen — nothing here renders it.
 *
 * Every tile below names a value the API actually returns:
 *
 * - base salary  -> `EmployeeEntity.salary` / `.salaryUnit` from `GET employees/me`, i.e. the
 *   figure an owner typed into EmployeeForm for this person. Hidden when it is 0/unset.
 * - branch tiles -> `GET employees/me/incomes`. These are BRANCH totals for the current
 *   calendar month, not personal ones: `DashboardService.employeeIncomes` filters orders by
 *   `restaurantId` + `restaurantBranchId` and by nothing else, so the labels say "your
 *   branch" and the note under the tiles repeats it.
 *
 * Deliberately absent: total income, bonuses, fines and days-off value. The API returns no
 * per-employee money at all — only `salary`, and Issues/DayOff rows that carry no computed
 * total — so there is nothing honest to put in those tiles. An absent number beats an
 * invented one.
 */

function Stat(props: {
  testId: string;
  title: string;
  value: number;
  format?: (value: number) => string;
  icon: React.ReactElement;
}) {
  return (
    <div
      className="employee-dashboard__stat_widget employee-dashboard__stat_widget--with-icon"
      data-testid={props.testId}
    >
      <h4>{props.title}</h4>
      <h3>{props.format ? props.format(props.value) : props.value}</h3>
      <div className="icon">{props.icon}</div>
    </div>
  );
}

interface MyEmploymentSummaryProps {
  employee: EmployeeEntity;
  /** Undefined while `me/incomes` is in flight or has failed — the salary tile still renders. */
  incomes: SelfEmployeeIncomes | undefined;
}

const MyEmploymentSummary = React.memo((props: MyEmploymentSummaryProps) => {
  const { t } = useTranslation();
  const { format } = useMoney();
  const { employee, incomes } = props;

  // Unlike EmployeeDashboard's version of this sum, seeded with 0: `sales` is [] for a month
  // with no orders, and `[].reduce(fn)` without an initial value throws.
  const branchSales = (incomes?.sales ?? []).reduce((total, day) => total + day.totalSales, 0);

  return (
    <div className="my-employment__summary">
      <h5 className="section__heading">{t('employee.myEmployment.summary')}</h5>
      <div className="stats">
        {Boolean(employee.salary) && (
          <Stat
            testId="my-employment-salary"
            title={
              employee.salaryUnit === 'H'
                ? t('employee.myEmployment.baseSalaryPerHour')
                : t('employee.myEmployment.baseSalaryPerMonth')
            }
            value={employee.salary}
            format={format}
            icon={<Portfolio size={36} />}
          />
        )}
        {incomes && (
          <Stat
            testId="my-employment-branch-orders"
            title={t('employee.myEmployment.branchOrders')}
            value={incomes.totalOrders}
            icon={<ShoppingCart size={36} />}
          />
        )}
        {incomes && (
          <Stat
            testId="my-employment-branch-slow-orders"
            title={t('employee.myEmployment.branchSlowOrders')}
            value={incomes.totalProcessingSlowOrders}
            icon={<Time size={36} />}
          />
        )}
        {incomes && (
          <Stat
            testId="my-employment-branch-sales"
            title={t('employee.myEmployment.branchSales')}
            value={branchSales}
            format={format}
            icon={<Money size={36} />}
          />
        )}
      </div>
      {incomes && <p className="my-employment__note">{t('employee.myEmployment.branchFiguresNote')}</p>}
    </div>
  );
});

export default MyEmploymentSummary;
